import { motion } from 'framer-motion';
import { ArrowUpRight, BookOpen, Feather, Sparkles } from 'lucide-react';
import { FadeUp } from './MaskReveal';

const PREVIEW = [
  {
    tag: 'Culture',
    date: 'Coming soon',
    title: 'What we mean when we say a company has a “strong culture.”',
    read: '8 min read',
    icon: Feather,
  },
  {
    tag: 'Leadership',
    date: 'Coming soon',
    title: 'The quiet arithmetic of building a C-suite that outlasts you.',
    read: '11 min read',
    icon: BookOpen,
  },
  {
    tag: 'Talent',
    date: 'Coming soon',
    title: 'Executive search in a world where the best people never apply.',
    read: '6 min read',
    icon: Sparkles,
  },
];

export default function ThoughtLeadership() {
  return (
    <section
      id="thought-leadership"
      data-testid="thought-leadership-section"
      className="relative bg-ink-800 py-16 md:py-24"
    >
      <div className="container-editorial">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <FadeUp>
              <span className="eyebrow">06 — Thought Leadership</span>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="display-heading mt-6 text-white text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
                Ideas worth reading.<br />
                <span className="text-white/40">Perspectives worth building on.</span>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <p className="text-white/60 text-base leading-relaxed max-w-sm">
              A curated journal of essays, playbooks and field notes from our advisors — publishing soon.
            </p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/10">
          {PREVIEW.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
                className="group relative bg-ink-900 p-8 md:p-10 min-h-[320px] flex flex-col justify-between overflow-hidden transition-colors duration-500 hover:bg-ink-700"
                data-testid={`thought-card-${i}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-signal-500">
                    {item.tag}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">
                    {item.date}
                  </span>
                </div>

                <div>
                  <Icon strokeWidth={1.25} className="h-6 w-6 text-white/60 mb-6 group-hover:text-signal-500 transition-colors duration-500" />
                  <h3 className="display-heading text-xl md:text-[22px] text-white leading-snug text-balance">
                    {item.title}
                  </h3>
                  <div className="mt-6 flex items-center justify-between text-white/50 text-[12px] font-mono uppercase tracking-[0.22em]">
                    <span>{item.read}</span>
                    <span className="inline-flex items-center gap-1.5 text-white/40 group-hover:text-signal-500 transition-colors duration-500">
                      Read
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </div>

                <span className="absolute bottom-0 left-0 h-px w-0 bg-signal-500 group-hover:w-full transition-[width] duration-700" />
              </motion.article>
            );
          })}
        </div>

        <FadeUp delay={0.2}>
          <div className="mt-10 md:mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-white/10 pt-6">
            <p className="text-white/65 text-sm md:text-base max-w-2xl">
              Our editorial journal launches soon. Get an email when the first essays go live — no
              newsletter fatigue, just work worth your inbox.
            </p>
            <a href="#contact-form" data-testid="thought-notify-cta" className="btn-pill btn-outline self-start md:self-auto">
              Notify me
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
